import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Wrench, Clock, Loader2, Phone, Mail, User, Copy, Check, LogOut } from 'lucide-react';
import ProviderAuthModal from '../components/ProviderAuthModal';
import ReuploadDocumentsModal from '../components/ReuploadDocumentsModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ZONES = ['CABA', 'Zona Norte', 'Zona Sur', 'Zona Oeste'];
const CATEGORIES = [
  'Plomería', 'Electricidad', 'Gasista', 'Pintura', 'Cerrajería',
  'Aire acondicionado', 'Arreglos Generales', 'Jardinería', 'Techista'
];

export default function ProfessionalView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zoneFilter, setZoneFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactData, setContactData] = useState(null);
  const [loadingContact, setLoadingContact] = useState(false);
  const [copied, setCopied] = useState(false);
  const [urgencyFilter, setUrgencyFilter] = useState('');

  // Provider auth state (persisted in localStorage)
  const [provider, setProvider] = useState(() => {
    try { return JSON.parse(localStorage.getItem('provider_data') || 'null'); } catch { return null; }
  });
  const [providerToken, setProviderToken] = useState(() => localStorage.getItem('provider_token') || null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingTicket, setPendingTicket] = useState(null);

  // Re-subida de documentos perdidos del volumen persistente
  const [showReuploadModal, setShowReuploadModal] = useState(false);
  const [documentsMissing, setDocumentsMissing] = useState(false);

  // Control de acceso de la plataforma
  const [platformEnabled, setPlatformEnabled] = useState(null); // null = cargando
  const [providerStatus, setProviderStatus] = useState(null);   // null = sin sesión o cargando

  // Verificar estado de la plataforma al montar
  useEffect(() => {
    axios.get(`${API}/providers/platform-status`)
      .then(r => setPlatformEnabled(r.data.provider_access_enabled))
      .catch(() => setPlatformEnabled(true)); // si falla, asumir habilitado
  }, []);

  // Verificar estado del proveedor cada vez que cambia el token
  useEffect(() => {
    if (!providerToken) { setProviderStatus(null); setDocumentsMissing(false); return; }
    axios.get(`${API}/providers/me`, {
      headers: { Authorization: `Bearer ${providerToken}` }
    })
      .then(r => {
        setProviderStatus({
          is_approved: r.data.is_approved,
          is_blocked: r.data.is_blocked,
          profile_completed: r.data.profile_completed,
        });
        const missing = Boolean(r.data.documents_missing);
        setDocumentsMissing(missing);
        // Aviso automático (una vez) al detectar documentos faltantes
        if (missing) setShowReuploadModal(true);
      })
      .catch(() => setProviderStatus(null));
  }, [providerToken]);

  useEffect(() => {
    fetchTickets();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoneFilter, categoryFilter, urgencyFilter]);

  // Auto-reveal: cuando el proveedor vuelve desde /provider-auth con ?autoReveal=ticketId
  const handleAutoReveal = useCallback(async (autoRevealId, token) => {
    // Esperar a que los tickets estén cargados
    const doReveal = (ticketList) => {
      const target = ticketList.find((t) => t.id === autoRevealId);
      if (target) {
        setSelectedTicket(target);
        let storedProvider = null;
        try {
          storedProvider = JSON.parse(localStorage.getItem('provider_data') || 'null');
        } catch {}
        if (storedProvider?.documents_missing) {
          setShowReuploadModal(true);
        } else {
          setShowConfirmModal(true);
        }
      }
      // Limpiar el query param para que no se re-ejecute en refresh
      setSearchParams({}, { replace: true });
    };

    // Si ya tenemos tickets, ejecutar directo; si no, esperar la carga
    setLoading(true);
    try {
      const response = await axios.get(`${API}/tickets/public`);
      setTickets(response.data);
      doReveal(response.data);
    } catch {
      toast.error('Error al cargar los trabajos');
    } finally {
      setLoading(false);
    }
  }, [setSearchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detectar retorno desde /provider-auth con autoReveal en query params
  useEffect(() => {
    const autoRevealId = searchParams.get('autoReveal');
    const token = localStorage.getItem('provider_token');
    if (autoRevealId && token) {
      // Sincronizar estado de provider desde localStorage
      try {
        const storedProvider = JSON.parse(localStorage.getItem('provider_data') || 'null');
        if (storedProvider) {
          setProvider(storedProvider);
          setProviderToken(token);
        }
      } catch {}
      handleAutoReveal(autoRevealId, token);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = {};
      if (zoneFilter && zoneFilter !== 'all') params.zone = zoneFilter;
      if (categoryFilter && categoryFilter !== 'all') params.category = categoryFilter;
      if (urgencyFilter && urgencyFilter !== 'all') params.urgency = urgencyFilter;
      
      const response = await axios.get(`${API}/tickets/public`, { params });
      setTickets(response.data);
    } catch (error) {
      if (error.response?.status === 503) {
        setPlatformEnabled(false);
      } else {
        console.error('Error fetching tickets:', error);
        toast.error('Error al cargar los trabajos');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewContact = (ticket) => {
    setSelectedTicket(ticket);
    if (!providerToken) {
      // Sin sesión: navegar a /provider-auth para todos los dispositivos
      navigate(`/provider-auth?ticketId=${ticket.id}`);
    } else {
      setShowConfirmModal(true);
    }
  };

  const handleAuthSuccess = (token, providerData) => {
    setProvider(providerData);
    setProviderToken(token);
    setShowAuthModal(false);
    if (pendingTicket) {
      setSelectedTicket(pendingTicket);
      setPendingTicket(null);
      setShowConfirmModal(true);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('provider_token');
    localStorage.removeItem('provider_data');
    setProvider(null);
    setProviderToken(null);
    toast.success('Sesión cerrada');
  };

  const handleConfirmYes = async () => {
    setShowConfirmModal(false);
    // Documentos perdidos: no se puede revelar hasta re-subirlos
    if (documentsMissing) {
      setShowReuploadModal(true);
      return;
    }
    setLoadingContact(true);
    try {
      const response = await axios.get(`${API}/tickets/${selectedTicket.id}/contact`, {
        headers: { Authorization: `Bearer ${providerToken}` }
      });
      setContactData(response.data);
      setShowContactModal(true);
    } catch (error) {
      if (error.response?.status === 401) {
        // Token expired — force re-login
        handleLogout();
        toast.error('Sesión expirada. Iniciá sesión nuevamente.');
        setPendingTicket(selectedTicket);
        setShowAuthModal(true);
      } else if (error.response?.status === 403 && error.response?.data?.detail === 'documents_required') {
        // Documentos perdidos del volumen: pedir re-subida
        setDocumentsMissing(true);
        setShowReuploadModal(true);
      } else if (error.response?.status === 403 && error.response?.data?.detail === 'profile_required') {
        navigate(`/completar-perfil?ticketId=${selectedTicket.id}`, { replace: true });
      } else {
        toast.error('Error al obtener los datos de contacto');
      }
    } finally {
      setLoadingContact(false);
    }
  };

  const handleConfirmNo = () => {
    setShowConfirmModal(false);
    setSelectedTicket(null);
  };

  const handleCopyData = async () => {
    if (!contactData) {
      toast.error('No hay datos para copiar');
      return;
    }
    
    const textToCopy = `Nombre: ${contactData.name || 'N/A'}
Teléfono: ${contactData.phone || 'N/A'}
Email: ${contactData.email || 'N/A'}
Categoría: ${contactData.category || 'N/A'}
Zona: ${contactData.zone || 'N/A'}
Descripción: ${contactData.description || 'N/A'}`;
    
    // Detectar si estamos dentro de un iframe
    const isInIframe = window.self !== window.top;
    
    try {
      // Si estamos en iframe, usar directamente execCommand (navigator.clipboard está bloqueado)
      if (isInIframe) {
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          const successful = document.execCommand('copy');
          if (successful) {
            setCopied(true);
            toast.success('Datos copiados al portapapeles');
            
            setTimeout(() => {
              setCopied(false);
            }, 2000);
          } else {
            throw new Error('execCommand failed');
          }
        } finally {
          document.body.removeChild(textArea);
        }
      } else {
        // No estamos en iframe, intentar Clipboard API primero
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(textToCopy);
          setCopied(true);
          toast.success('Datos copiados al portapapeles');
          
          setTimeout(() => {
            setCopied(false);
          }, 2000);
        } else {
          // Fallback a execCommand si Clipboard API no está disponible
          const textArea = document.createElement('textarea');
          textArea.value = textToCopy;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          
          try {
            const successful = document.execCommand('copy');
            if (successful) {
              setCopied(true);
              toast.success('Datos copiados al portapapeles');
              
              setTimeout(() => {
                setCopied(false);
              }, 2000);
            } else {
              throw new Error('execCommand failed');
            }
          } finally {
            document.body.removeChild(textArea);
          }
        }
      }
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast.error('No se pudo copiar. Intenta seleccionar y copiar manualmente.');
    }
  };

  const timeAgo = (dateString) => {
    try {
      const created = new Date(dateString);
      const now = new Date();
      const diffMs = now - created;
      const diffMinutes = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMinutes < 1) return 'Hace un momento';
      if (diffMinutes < 60) return `Hace ${diffMinutes} minuto${diffMinutes !== 1 ? 's' : ''}`;
      if (diffHours < 24) return `Hace ${diffHours} hora${diffHours !== 1 ? 's' : ''}`;
      return `Hace ${diffDays} día${diffDays !== 1 ? 's' : ''}`;
    } catch {
      return dateString;
    }
  };

  // ── Pantallas de control de acceso ──────────────────────────────────────
  const MaintenanceScreen = () => (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6 text-center">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 bg-gray-200">
        <svg className="w-8 h-8 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-800 mb-3">Mantenimiento</h2>
      <p className="text-gray-500 max-w-sm text-sm leading-relaxed">
        La web está en mantenimiento temporalmente, te avisaremos cuando puedas volver a usarla.
      </p>
    </div>
  );

  const PendingApprovalScreen = () => (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6 text-center">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: 'var(--primary-light)' }}>
        <svg className="w-8 h-8" style={{ color: 'var(--primary-strong)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-800 mb-3">Cuenta en revisión</h2>
      <p className="text-gray-500 max-w-sm text-sm leading-relaxed">
        Tu cuenta está en proceso de aprobación. Te avisaremos cuando esté activa.
      </p>
      <button
        onClick={() => navigate('/')}
        className="mt-6 text-sm text-gray-400 hover:text-gray-600 underline"
      >
        Volver a la página de inicio
      </button>
    </div>
  );

  // Cargando estado de plataforma
  if (platformEnabled === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Mantenimiento global
  if (!platformEnabled) return <MaintenanceScreen />;

  // Proveedor logueado con estado de acceso cargado
  if (providerToken && providerStatus) {
    if (providerStatus.is_blocked) return <MaintenanceScreen />;
    if (!providerStatus.is_approved) return <PendingApprovalScreen />;
  }
  // ────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50" data-testid="professional-view-page">
      {/* Header */}
      <header className="bg-white px-4 py-5 border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/')}
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl sm:text-2xl font-bold" style={{ color: 'var(--primary-strong)' }}>Trabajos Disponibles</h1>
          </div>

          {/* Provider auth section */}
          {provider ? (
            <div className="flex items-center gap-3" data-testid="provider-header">
              <div className="flex items-center gap-2">
                <div
                  data-testid="provider-avatar"
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                  style={{ backgroundColor: provider.avatar_color }}
                >
                  {provider.username.charAt(0).toUpperCase()}
                </div>
                <span className="text-sm font-medium text-gray-700 hidden sm:block" data-testid="provider-username">
                  {provider.username}
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                data-testid="logout-btn"
                title="Cerrar sesión"
              >
                <LogOut className="w-4 h-4 text-gray-500" />
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigate('/provider-auth');
              }}
              data-testid="login-btn"
              className="text-sm"
            >
              Iniciar sesión
            </Button>
          )}
        </div>
      </header>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Select value={zoneFilter} onValueChange={setZoneFilter}>
                <SelectTrigger data-testid="zone-filter">
                  <SelectValue placeholder="Todas las zonas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las zonas</SelectItem>
                  {ZONES.map((zone) => (
                    <SelectItem key={zone} value={zone}>{zone}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger data-testid="category-filter">
                  <SelectValue placeholder="Todas las categorías" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Select value={urgencyFilter} onValueChange={setUrgencyFilter}>
                <SelectTrigger data-testid="urgency-filter">
                  <SelectValue placeholder="Todas las urgencias" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las urgencias</SelectItem>
                  <SelectItem value="URGENTE">🚨 Solo urgentes</SelectItem>
                  <SelectItem value="NORMAL">No urgentes</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Tickets List */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        <section
          className="mb-6 rounded-2xl border border-orange-200 bg-orange-50 px-5 py-6 text-gray-700 shadow-sm sm:px-8"
          data-testid="operations-closure-banner"
        >
          <div className="max-w-4xl space-y-4 text-sm leading-relaxed sm:text-base">
            <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Gracias por ser parte de Providers ❤️</h2>
            <p>
              Después de mucho esfuerzo e intentar hacer crecer este proyecto, tomamos la difícil decisión de cerrar nuestras operaciones.
            </p>
            <p>
              Creamos Providers con la intención de ayudar a los profesionales de oficios a conseguir más oportunidades, crecer y trabajar en mejores condiciones. Siempre buscamos priorizar sus beneficios, por eso elegimos no cobrar comisiones por los trabajos realizados.
            </p>
            <p>
              Aunque no logramos llegar tan lejos como soñábamos, estamos profundamente agradecidos por la confianza y por habernos acompañado en este camino.
            </p>
            <p className="font-semibold text-gray-900">
              Queremos que sigas creciendo y encontrando nuevas oportunidades. Por eso, te recomendamos conocer QXM (Quién x Mí), una plataforma que ofrece múltiples beneficios para los profesionales, incluyendo herramientas, capacitación y acceso a servicios financieros.
            </p>
            <p>
              <a
                href="https://www.qxm.com.ar/"
                target="_blank"
                rel="noreferrer"
                className="font-bold text-blue-700 underline decoration-blue-700 underline-offset-2 hover:text-blue-900"
                data-testid="qxm-link"
              >
                👉 Conocé QXM y descubrí sus beneficios
              </a>
            </p>
            <p>
              Gracias por habernos permitido ser parte de tu camino profesional.
            </p>
            <p>
              Con mucho agradecimiento,<br />
              El equipo de Providers
            </p>
            <p className="border-t border-orange-200 pt-4 font-semibold text-gray-900">
              📅 Providers cierra sus operaciones el 15 de octubre de 2026.
            </p>
          </div>
        </section>
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary-strong)' }} />
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg">No hay trabajos disponibles con estos filtros</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {tickets.map((ticket) => (
              <div
                key={ticket.id}
                data-testid={`ticket-card-${ticket.id}`}
                className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg transition-shadow"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    {/* Category, Zone & Urgency */}
                    <div className="flex flex-wrap items-center gap-2">
                      {ticket.urgency === 'URGENTE' && (
                        <span
                          data-testid={`urgency-badge-${ticket.id}`}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-red-100 text-red-700 border border-red-200"
                        >
                          🚨 URGENTE
                        </span>
                      )}
                      <span 
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium text-white"
                        style={{ backgroundColor: 'var(--primary-strong)' }}
                      >
                        <Wrench className="w-4 h-4" />
                        {ticket.category}
                      </span>
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                        <MapPin className="w-4 h-4" />
                        {ticket.zone}{ticket.neighborhood ? ` | ${ticket.neighborhood}` : ''}
                      </span>
                    </div>

                    {/* Description */}
                    <p className="text-gray-700 leading-relaxed">{ticket.description}</p>

                    {/* Meta info */}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {timeAgo(ticket.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* CTA Button */}
                  <Button
                    data-testid={`view-contact-btn-${ticket.id}`}
                    onClick={() => handleViewContact(ticket)}
                    className="sm:self-center whitespace-nowrap"
                    style={{ backgroundColor: 'var(--dark-primary)' }}
                  >
                    Ver Contacto
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Confirm Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent data-testid="confirm-modal">
          <DialogHeader>
            <DialogTitle>¿Te interesa este trabajo?</DialogTitle>
            <DialogDescription>
              Al confirmar, podrás ver los datos de contacto del cliente
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              data-testid="confirm-no-btn"
              variant="outline"
              onClick={handleConfirmNo}
            >
              No
            </Button>
            <Button
              data-testid="confirm-yes-btn"
              onClick={handleConfirmYes}
              style={{ backgroundColor: 'var(--primary-strong)' }}
            >
              Sí, me interesa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Modal */}
      <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
        <DialogContent data-testid="contact-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Datos de Contacto</DialogTitle>
            <DialogDescription>
              Contactá al cliente directamente
            </DialogDescription>
          </DialogHeader>
          
          {loadingContact ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary-strong)' }} />
            </div>
          ) : contactData && (
            <div className="space-y-4 py-4">
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <User className="w-5 h-5 mt-0.5" style={{ color: 'var(--primary-strong)' }} />
                <div>
                  <p className="text-sm text-gray-500 mb-1">Nombre</p>
                  <p className="font-medium" data-testid="contact-name">{contactData.name}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <Phone className="w-5 h-5 mt-0.5" style={{ color: 'var(--primary-strong)' }} />
                <div>
                  <p className="text-sm text-gray-500 mb-1">Teléfono</p>
                  <a 
                    href={`tel:${contactData.phone}`}
                    className="font-medium hover:underline"
                    data-testid="contact-phone"
                  >
                    {contactData.phone}
                  </a>
                </div>
              </div>
              
              <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                <Mail className="w-5 h-5 mt-0.5" style={{ color: 'var(--primary-strong)' }} />
                <div>
                  <p className="text-sm text-gray-500 mb-1">Email</p>
                  <a 
                    href={`mailto:${contactData.email}`}
                    className="font-medium hover:underline break-all"
                    data-testid="contact-email"
                  >
                    {contactData.email}
                  </a>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              data-testid="copy-data-btn"
              onClick={handleCopyData}
              variant="outline"
              disabled={!contactData}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar datos
                </>
              )}
            </Button>
            <Button
              data-testid="close-contact-modal-btn"
              onClick={() => setShowContactModal(false)}
              style={{ backgroundColor: 'var(--primary-strong)' }}
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Provider Auth Modal */}
      <ProviderAuthModal
        open={showAuthModal}
        onClose={() => { setShowAuthModal(false); setPendingTicket(null); }}
        onSuccess={handleAuthSuccess}
      />

      {/* Re-subida de documentos perdidos del volumen */}
      <ReuploadDocumentsModal
        open={showReuploadModal}
        token={providerToken}
        onClose={() => setShowReuploadModal(false)}
        onSuccess={() => {
          setDocumentsMissing(false);
          setShowReuploadModal(false);
          try {
            const storedProvider = JSON.parse(localStorage.getItem('provider_data') || 'null');
            if (storedProvider) {
              localStorage.setItem('provider_data', JSON.stringify({
                ...storedProvider,
                documents_missing: false,
                verification_documents_completed: true,
              }));
            }
          } catch {}
          const ticketQuery = selectedTicket?.id ? `?ticketId=${selectedTicket.id}` : '';
          navigate(`/completar-perfil${ticketQuery}`, { replace: true });
        }}
      />
    </div>
  );
}
